import { useMemo } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { toAuthenticatedUser } from "@/lib/utils/crm/user-adapters";
import type { AuthenticatedUser } from "@/lib/types/unified";

/**
 * Auth and permission logic for UniversalEntityPage
 * Extracts authentication, authorization, and responsive behavior
 */
export function useUniversalEntityPageAuth() {
  // Auth hooks
  const {
    appUser: rawAppUser,
    isAuthenticated,
    isClientUser,
    isLoading: authLoading,
  } = useUniversalAuth();
  // Memoized: `toAuthenticatedUser` SPREADS into a new object, so calling it bare
  // here would hand every downstream dep array a fresh identity each render.
  // That is what silently killed the SimpleTable stable context slice.
  const appUser = useMemo(() => toAuthenticatedUser(rawAppUser), [rawAppUser]);
  const { isEmployee, isAdmin, isWTCCRM, isWTCEmployee, isAdmin: isAdminRole } = useRoleCheck();

  // Responsive behavior
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Permission calculations
  const canCreate = useMemo(() => {
    if (isWTCCRM || isAdminRole) return true;
    const employeeCreateEntities = ["tickets", "time_entries", "tasks"];
    // Note: entityName will be passed separately, so we can't check it here
    // This will be computed in the handlers hook instead
    return isWTCEmployee;
  }, [isWTCCRM, isAdminRole, isWTCEmployee]);

  return {
    appUser,
    isAuthenticated,
    isClientUser,
    authLoading,
    isEmployee,
    isAdmin,
    isWTCCRM,
    isWTCEmployee,
    isAdminRole,
    isMobile,
    canCreate,
  };
}
